import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { parseTimeToSeconds, formatTo12Hour, Schedule } from '@thekwallace/backtime-shared';

type DayOfWeek = 'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday';

const DAYS: DayOfWeek[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Full 24-hour range
const START_HOUR = 0;
const END_HOUR = 24;

// Default scroll position (6am)
const DEFAULT_SCROLL_HOUR = 6;

interface HoveredCell {
  day: DayOfWeek;
  time: string;
}

interface HeaderLabel {
  index: number;
  time: string;
}

interface ScheduleChartProps {
  schedule: Schedule;
  onScheduleChange: (schedule: Schedule) => void;
  snapMinutes?: number;
  readOnly?: boolean;
}

/**
 * Schedule chart component for managing day-specific service times
 * Displays a grid with days on Y-axis and times on X-axis
 */
const ScheduleChart: React.FC<ScheduleChartProps> = ({
  schedule,
  onScheduleChange,
  snapMinutes = 30,
  readOnly = false
}) => {
  const [hoveredCell, setHoveredCell] = useState<HoveredCell | null>(null);
  const chartRef = useRef<HTMLDivElement>(null);

  // Generate time slots based on snap value
  const timeSlots: string[] = [];
  for (let hour = START_HOUR; hour < END_HOUR; hour++) {
    for (let minute = 0; minute < 60; minute += snapMinutes) {
      const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:00`;
      timeSlots.push(timeString);
    }
  }

  // Scroll to default position (6am) on mount
  useEffect(() => {
    if (chartRef.current) {
      const scrollContainer = chartRef.current;
      const totalSlots = timeSlots.length;
      const defaultSlotIndex = (DEFAULT_SCROLL_HOUR - START_HOUR) * (60 / snapMinutes);
      const scrollPercentage = defaultSlotIndex / totalSlots;
      const maxScroll = scrollContainer.scrollWidth - scrollContainer.clientWidth;
      scrollContainer.scrollLeft = maxScroll * scrollPercentage;
    }
  }, [snapMinutes, timeSlots.length]);

  // Get times for a specific day
  const getTimesForDay = (day: DayOfWeek): string[] => {
    return schedule[day] || [];
  };

  // Check if a time slot is scheduled for a day
  const isTimeScheduled = (day: DayOfWeek, time: string): boolean => {
    const times = getTimesForDay(day);
    return times.includes(time);
  };

  // Toggle a time slot for a day
  const toggleTime = (day: DayOfWeek, time: string): void => {
    const times = getTimesForDay(day);
    let newTimes: string[];

    if (times.includes(time)) {
      // Remove the time
      newTimes = times.filter(t => t !== time);
    } else {
      // Add the time and sort
      newTimes = [...times, time].sort();
    }

    onScheduleChange({
      ...schedule,
      [day]: newTimes
    });
  };

  // Format time for display (e.g., "8:30 AM")
  const formatTimeLabel = (time: string): string => {
    return formatTo12Hour(parseTimeToSeconds(time), true).replace(':00 ', ' ');
  };

  // Get header labels (only show some to avoid crowding)
  const getHeaderLabels = (): HeaderLabel[] => {
    const labels: HeaderLabel[] = [];
    const step = Math.max(1, Math.floor(timeSlots.length / 8)); // Show ~8 labels
    for (let i = 0; i < timeSlots.length; i += step) {
      labels.push({ index: i, time: timeSlots[i] });
    }
    return labels;
  };

  return (
    <div className="bg-gray-100/50 dark:bg-gray-900/50 rounded-lg p-4 border border-gray-300 dark:border-gray-700">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-300">Weekly Schedule</h3>
        <div className="text-xs text-gray-500">
          {readOnly ? 'View only' : `Click to add/remove times (snap: ${snapMinutes} min)`}
        </div>
      </div>

      <div className="overflow-x-auto" ref={chartRef}>
        <div className="min-w-[400px] sm:min-w-[500px] md:min-w-[600px]">
          {/* Time header */}
          <div className="flex mb-2">
            <div className="w-10 sm:w-12 flex-shrink-0" /> {/* Day label spacer */}
            <div className="flex-1 flex relative">
              {getHeaderLabels().map(({ index, time }) => (
                <div
                  key={time}
                  className="absolute text-xs text-gray-500 transform -translate-x-1/2"
                  style={{ left: `${(index / timeSlots.length) * 100}%` }}
                >
                  {formatTimeLabel(time)}
                </div>
              ))}
            </div>
          </div>

          {/* Chart rows */}
          <div className="space-y-1 mt-6">
            {DAYS.map((day, dayIndex) => (
              <div key={day} className="flex items-center">
                {/* Day label */}
                <div className="w-10 sm:w-12 flex-shrink-0 text-xs font-medium text-gray-500 dark:text-gray-400">
                  {DAY_LABELS[dayIndex]}
                </div>

                {/* Time slots */}
                <div className="flex-1 flex h-6 sm:h-8 bg-gray-200 dark:bg-gray-800 rounded overflow-hidden">
                  {timeSlots.map((time) => {
                    const isScheduled = isTimeScheduled(day, time);
                    const isHovered = hoveredCell?.day === day && hoveredCell?.time === time;

                    return (
                      <div
                        key={time}
                        className={`flex-1 border-r border-gray-300 dark:border-gray-700 last:border-r-0 transition-colors ${
                          isScheduled
                            ? 'bg-blue-600' + (readOnly ? '' : ' hover:bg-blue-500')
                            : isHovered && !readOnly
                            ? 'bg-gray-300 dark:bg-gray-600'
                            : readOnly ? '' : 'hover:bg-gray-300 dark:hover:bg-gray-700'
                        } ${readOnly ? '' : 'cursor-pointer'}`}
                        onClick={() => !readOnly && toggleTime(day, time)}
                        onMouseEnter={() => !readOnly && setHoveredCell({ day, time })}
                        onMouseLeave={() => setHoveredCell(null)}
                        title={`${DAY_LABELS[dayIndex]} ${formatTimeLabel(time)}`}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Legend and scheduled times summary */}
      <div className="mt-4 pt-4 border-t border-gray-300 dark:border-gray-700">
        <div className="text-xs text-gray-500 mb-2">Scheduled times:</div>
        <div className="space-y-1">
          {DAYS.map((day, dayIndex) => {
            const times = getTimesForDay(day);
            if (times.length === 0) return null;

            return (
              <div key={day} className="flex items-center gap-2 text-xs">
                <span className="text-gray-500 dark:text-gray-400 w-12">{DAY_LABELS[dayIndex]}:</span>
                <div className="flex flex-wrap gap-1">
                  {times.map(time => (
                    <span
                      key={time}
                      className="px-2 py-0.5 bg-blue-600/30 text-blue-600 dark:text-blue-400 rounded flex items-center gap-1"
                    >
                      {formatTimeLabel(time)}
                      {!readOnly && (
                        <button
                          onClick={() => toggleTime(day, time)}
                          className="hover:text-red-400 transition-colors"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
          {DAYS.every(day => getTimesForDay(day).length === 0) && (
            <div className="text-gray-500 text-xs italic">No times scheduled</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScheduleChart;
